<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="modelValue"
        class="modal-overlay"
        :class="{ 'modal-overlay--fullscreen': fullScreen, 'modal-overlay--sheet-mobile': !fullScreen }"
        :style="overlayStyle"
      >
        <div
          v-if="!fullScreen"
          class="modal-overlay__backdrop"
          aria-hidden="true"
          :style="modalBackdropDimStyle"
          @click="handleBackdrop"
        />
        <div
          ref="modalCardRef"
          class="modal-card"
          :class="{ 'full-screen': fullScreen, 'modal-card--sheet-mobile': !fullScreen, 'modal-card--sheet-expanded': mobileSheetExpanded }"
          role="dialog"
          aria-modal="true"
          :aria-label="dialogAriaLabel"
          :style="modalCardStyle"
          @click.stop
        >
          <div
            v-if="!fullScreen"
            class="modal-sheet-handle-wrap"
            @pointerdown="startMobileSheetDrag"
          >
            <span class="modal-sheet-handle" aria-hidden="true"></span>
          </div>
          <div
            class="modal-head"
            :class="{ 'modal-head--fullscreen': fullScreen, 'modal-head--with-left-slot': !fullScreen && hasHeaderLeftSlot }"
          >
            <div v-if="fullScreen" class="modal-head__rail">
              <button
                class="modal-close modal-close--fullscreen-back"
                type="button"
                :disabled="closeDisabled"
                :aria-disabled="closeDisabled ? 'true' : undefined"
                @click="emitClose('close_button')"
              >
                <Icon name="arrow-circle-left" size="16" class="modal-back-icon" />
                <span class="modal-back-label">Назад</span>
              </button>
              <div v-if="showHeadTitle" class="modal-title-wrapper">
                <div class="modal-title">{{ title }}</div>
                <div v-if="subtitle" class="modal-subtitle">{{ subtitle }}</div>
              </div>
              <slot name="header"></slot>
            </div>
            <template v-else>
              <div class="modal-head__leading">
                <slot name="headerLeft"></slot>
                <div v-if="showHeadTitle" class="modal-title-wrapper">
                  <div class="modal-title">{{ title }}</div>
                  <div v-if="subtitle" class="modal-subtitle">{{ subtitle }}</div>
                </div>
              </div>
              <button
                v-if="!fullScreen"
                type="button"
                class="modal-sheet-expand-toggle"
                :aria-expanded="mobileSheetExpanded ? 'true' : 'false'"
                @click="toggleSheetExpanded"
              >
                {{ mobileSheetExpanded ? "Свернуть" : "На весь экран" }}
              </button>
              <button class="modal-close" type="button" @click="emitClose('close_button')" aria-label="Закрыть">
                <Icon name="x-circle" size="28" />
              </button>
              <slot name="header"></slot>
            </template>
          </div>

          <div class="modal-body">
            <slot></slot>
          </div>

          <footer v-if="$slots.footer" class="modal-footer">
            <slot name="footer"></slot>
          </footer>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, toRef, useSlots, watch } from "vue";
import Icon from "@/components/ui/Icon.vue";
import type { ModalCloseReason } from "@/components/modal/modalCloseReason";
import { useFocusTrapDialog } from "@/composables/useFocusTrapDialog";

import "./Modal.scss";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Modal",
  },
  subtitle: {
    type: String,
    default: "",
  },
  icon: {
    type: String,
    default: "",
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true,
  },
  closeOnEsc: {
    type: Boolean,
    default: true,
  },
  maxWidth: {
    type: String,
    default: "auto",
  },
  fullScreen: {
    type: Boolean,
    default: false,
  },
  closeDisabled: {
    type: Boolean,
    default: false,
  },
  mobileSheetExpanded: {
    type: Boolean,
    default: false,
  },
  /**
   * Опциональный z-index для оверлея. Используйте, когда модалка должна стекаться поверх
   * других модалок (например, BalanceModal, открытая поверх full-screen CreateGoodDialog).
   * По умолчанию применяется значение из Modal.scss (.modal-overlay { z-index: 9999 }).
   */
  zIndex: {
    type: Number,
    default: undefined,
  },
});

const modalCardRef = ref<HTMLElement | null>(null);
useFocusTrapDialog(toRef(props, "modelValue"), modalCardRef);
const slots = useSlots();

const showHeadTitle = computed(() => Boolean(props.title?.trim()) || Boolean(props.subtitle?.trim()));
const hasHeaderLeftSlot = computed(() => Boolean(slots.headerLeft));
const mobileSheetOffsetY = ref(0);
const mobileSheetDragging = ref(false);
const mobileSheetSettling = ref(false);
const mobileSheetExpanded = ref(false);

const dialogAriaLabel = computed(() => {
  const t = props.title?.trim();
  if (t) return t;
  return "Диалог";
});

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "close", reason?: ModalCloseReason): void;
  (e: "sheet-interaction", payload: { action: "expand" | "collapse" }): void;
}>();

const thisInstance = getCurrentInstance();

const emitClose = (reason: ModalCloseReason = "unknown") => {
  if (props.closeDisabled) {
    return;
  }
  const hasCustomClose = thisInstance?.vnode?.props?.onClose;

  if (hasCustomClose) {
    emit("close", reason); // управление у родителя
  } else {
    emit("update:modelValue", false); // автозакрытие
  }
};

const isMobileSheetMode = () => {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= 640 && !props.fullScreen;
};

/** Затемнение фона при перетаскивании mobile sheet (как раньше на overlay). */
const modalBackdropDimStyle = computed(() => {
  if (!isMobileSheetMode()) return undefined;
  if (!mobileSheetDragging.value && mobileSheetOffsetY.value === 0 && !mobileSheetSettling.value) return undefined;
  const progress = Math.min(1, Math.max(0, mobileSheetOffsetY.value) / 320);
  return {
    opacity: String(1 - progress * 0.6),
    transition: mobileSheetDragging.value ? "none" : "opacity 240ms ease",
  };
});

const overlayStyle = computed<Record<string, string> | undefined>(() => {
  if (props.zIndex === undefined || props.zIndex === null) return undefined;
  return { zIndex: String(props.zIndex) };
});

const modalCardStyle = computed(() => {
  if (props.fullScreen) return undefined;
  const style: Record<string, string> = { maxWidth: props.maxWidth };
  if (!isMobileSheetMode()) return style;
  style.height = mobileSheetExpanded.value ? "100dvh" : "80vh";
  style.maxHeight = mobileSheetExpanded.value ? "100dvh" : "80vh";
  if (mobileSheetDragging.value || mobileSheetSettling.value || mobileSheetOffsetY.value > 0) {
    style.transform = `translate3d(0, ${mobileSheetOffsetY.value}px, 0)`;
    style.transition = mobileSheetDragging.value ? "none" : "transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1)";
  } else if (mobileSheetOffsetY.value < 0) {
    style.transform = `translate3d(0, ${mobileSheetOffsetY.value}px, 0)`;
    style.transition = mobileSheetDragging.value ? "none" : "transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1)";
  }
  return style;
});

const handleBackdrop = () => {
  if (!props.closeOnBackdrop || props.closeDisabled) {
    return;
  }

  emitClose("backdrop");
};

const startMobileSheetDrag = (event: PointerEvent) => {
  if (!isMobileSheetMode()) return;
  if (event.pointerType === "mouse" && event.button !== 0) return;
  if (props.closeDisabled) return;

  const target = event.currentTarget;
  if (!(target instanceof HTMLElement)) return;

  const startY = event.clientY;
  const startAt = performance.now();
  let lastY = startY;

  mobileSheetDragging.value = true;
  mobileSheetSettling.value = false;
  target.setPointerCapture(event.pointerId);

  const onMove = (moveEvent: PointerEvent) => {
    const rawDelta = moveEvent.clientY - startY;
    const delta = Math.max(-220, Math.min(320, rawDelta));
    mobileSheetOffsetY.value = delta;
    lastY = moveEvent.clientY;
  };

  const onEnd = () => {
    target.removeEventListener("pointermove", onMove);
    target.removeEventListener("pointerup", onEnd);
    target.removeEventListener("pointercancel", onEnd);

    const elapsed = Math.max(1, performance.now() - startAt);
    const distance = mobileSheetOffsetY.value;
    const velocity = (lastY - startY) / elapsed;
    const shouldExpand = !mobileSheetExpanded.value && distance < -80;
    const shouldCollapse = mobileSheetExpanded.value && distance > 110;
    const shouldCloseCollapsed = !mobileSheetExpanded.value && (distance > 120 || velocity > 0.55);
    const shouldCloseExpanded = mobileSheetExpanded.value && (distance > 220 || velocity > 0.9);

    mobileSheetDragging.value = false;
    mobileSheetSettling.value = true;

    if (shouldExpand) {
      mobileSheetExpanded.value = true;
      mobileSheetOffsetY.value = 0;
      window.setTimeout(() => {
        mobileSheetSettling.value = false;
      }, 260);
      return;
    }

    if (shouldCloseExpanded) {
      emitClose("sheet_swipe");
      mobileSheetOffsetY.value = 0;
      mobileSheetSettling.value = false;
      return;
    }

    if (shouldCollapse) {
      mobileSheetExpanded.value = false;
      mobileSheetOffsetY.value = 0;
      window.setTimeout(() => {
        mobileSheetSettling.value = false;
      }, 260);
      return;
    }

    if (shouldCloseCollapsed) {
      emitClose("sheet_swipe");
      mobileSheetOffsetY.value = 0;
      mobileSheetSettling.value = false;
      return;
    }

    mobileSheetOffsetY.value = 0;
    window.setTimeout(() => {
      mobileSheetSettling.value = false;
    }, 260);
  };

  target.addEventListener("pointermove", onMove);
  target.addEventListener("pointerup", onEnd);
  target.addEventListener("pointercancel", onEnd);
};

const toggleSheetExpanded = () => {
  if (!isMobileSheetMode()) return;
  const nextExpanded = !mobileSheetExpanded.value;
  mobileSheetExpanded.value = nextExpanded;
  mobileSheetOffsetY.value = 0;
  emit("sheet-interaction", { action: nextExpanded ? "expand" : "collapse" });
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!props.modelValue || !props.closeOnEsc || props.closeDisabled) {
    return;
  }

  if (event.key === "Escape") {
    emitClose("escape");
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleKeydown);
});

watch(
  () => props.modelValue,
  (open) => {
    if (open) return;
    mobileSheetOffsetY.value = 0;
    mobileSheetDragging.value = false;
    mobileSheetSettling.value = false;
    mobileSheetExpanded.value = false;
  }
);

watch(
  () => props.mobileSheetExpanded,
  (expanded) => {
    if (!isMobileSheetMode()) return;
    mobileSheetExpanded.value = expanded;
    mobileSheetOffsetY.value = 0;
  }
);
</script>
